import { pdfSheets } from './pdfSheets';

// Letter prices for INFORMATIONAL copy only (template setup hints). The binding price always comes
// from the backend cost-estimate response at send time — never hardcode prices in JSX strings.
// KEEP IN SYNC with the backend constants in
// cnc-worker-backend/src/features/postMailing/billing/postMailingBilling.js
// (POSTMAILING_PRICE_PER_UNIT / _DUPLEX / _MULTI_SHEET_BASE / _PER_EXTRA_SHEET). Single sheet:
// simplex/duplex base; 2+ sheets: multi-sheet base with backs INCLUDED + surcharge per sheet
// beyond the second.
export const PM_LETTER_PRICE_EUR = 1.0;
export const PM_LETTER_PRICE_DUPLEX_EUR = 1.3;
export const PM_LETTER_PRICE_MULTI_SHEET_BASE_EUR = 1.6;
export const PM_LETTER_PRICE_PER_EXTRA_SHEET_EUR = 0.1;

const euro = new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' });

/** "1,00 €" — simplex per-letter price for German UI copy. */
export const PM_LETTER_PRICE_LABEL = euro.format(PM_LETTER_PRICE_EUR);
/** "1,30 €" — duplex per-letter price for German UI copy. */
export const PM_LETTER_PRICE_DUPLEX_LABEL = euro.format(PM_LETTER_PRICE_DUPLEX_EUR);
/** "1,60 €" — 2-sheet letter price (backs included), for German UI copy. */
export const PM_LETTER_PRICE_MULTI_SHEET_BASE_LABEL = euro.format(PM_LETTER_PRICE_MULTI_SHEET_BASE_EUR);
/** "0,10 €" — surcharge per sheet beyond the second, for German UI copy. */
export const PM_LETTER_PRICE_PER_EXTRA_SHEET_LABEL = euro.format(PM_LETTER_PRICE_PER_EXTRA_SHEET_EUR);

/**
 * Whether a design prints double-sided: an uploaded PDF template with a back page on ANY sheet.
 * The block model is always simplex. Mirrors the backend's `isDuplexDesign` in
 * postMailing/helpers/designKind.js — keep the two in step.
 * @param {object} [designData]
 * @returns {boolean}
 */
export const isDuplexDesign = (designData) => pdfSheets(designData).some((sheet) => Boolean(sheet.backAssetId));

/**
 * Special-format PDFs (non-A4 or multi-page files) only ship via the bulk flow's manual offer — they
 * cannot be sent automatically from the CRM.
 * @param {object} [designData]
 * @returns {boolean}
 */
export const isAutoSendable = (designData) => !(designData?.kind === 'pdf' && designData.pdf?.apiEligible === false);

/**
 * Per-letter price label for a template's design ("1,00 €" / "1,30 €" / "1,70 €" for 3 sheets):
 * single sheet → simplex/duplex base; 2+ sheets → multi-sheet base (backs included) plus the
 * per-sheet surcharge beyond the second.
 * @param {object} [designData]
 * @returns {string}
 */
export const letterPriceLabel = (designData) => {
	const sheetCount = pdfSheets(designData).length;
	if (sheetCount > 1) {
		return euro.format(
			PM_LETTER_PRICE_MULTI_SHEET_BASE_EUR + (sheetCount - 2) * PM_LETTER_PRICE_PER_EXTRA_SHEET_EUR,
		);
	}
	return isDuplexDesign(designData) ? PM_LETTER_PRICE_DUPLEX_LABEL : PM_LETTER_PRICE_LABEL;
};
